// Aba Amigos (canvas F16), de cima para baixo: busca por apelido, PEDIDOS DE AMIZADE (com Aceitar e Recusar) e SEUS
// AMIGOS com rosto, apelido, trio fixo e status, na ordem que a API manda (em sala aberta, jogando agora, online e
// visto há). Chamar para a mesa vale quando você está numa sala na espera e com vaga; fora de sala, o botão fica
// desabilitado com a dica. Cada amigo tem o botão do WhatsApp (aberto só com os dois mostrando o número) e, no fim, a
// chave "Mostrar meu WhatsApp para amigos". A sala de espera abre a mesma lista numa folha (FolhaChamarAmigos).
import { SeloNoTopo } from '../comum/SeloGratis.tsx';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { rotuloDaDivisao } from '@truco6/gamificacao';
import type { Plano } from '@truco6/planos';
import {
  type AmigoNaLista, type Encontrado, type MinhaSala, type PedidoDeAmizade, buscarJogadores, chamarParaMesa, lerAmigos, lerPedidos,
} from '../comum/amigos.ts';
import { ErroDaApi } from '../comum/api.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { BotaoAmizade, IconeAmigos } from '../comum/BotaoAmizade.tsx';
import { BotaoPerfil } from '../comum/BotaoPerfil.tsx';
import { BotaoTrio } from '../comum/BotaoTrio.tsx';
import type { Sessao } from '../comum/conta.ts';
import { textoVisto } from '../comum/quando.ts';
import { definirRelacao, useRelacao } from '../comum/relacoes.ts';
import { ir } from '../comum/rotas.ts';
import { irParaMesa } from '../comum/limite.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { usePresenca } from '../comum/presenca.ts';
import { useAgora } from '../comum/useMesa.ts';
import { BotaoWhatsapp, ChaveWhatsapp } from '../comum/Whatsapp.tsx';
import { Escudo } from '../liga/trios.tsx';
import { Tela } from './comum.tsx';

const ATUALIZAR_MS = 30_000;
const DICA_SEM_SALA = 'Entre numa sala para chamar';

// A lista de amigos e a sua sala, relida a cada 30 s com a tela aberta (os status mudam).
function useAmigos(sessao: Sessao) {
  const [dados, setDados] = useState<{ amigos: AmigoNaLista[]; sala: MinhaSala | null } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [vez, setVez] = useState(0); // recarregar() lê de novo na hora
  useEffect(() => {
    let vivo = true;
    const ler = () => lerAmigos(sessao).then((r) => {
      if (!vivo) return;
      for (const a of r.amigos) definirRelacao(a.usuario, 'amigos');
      setDados({ amigos: r.amigos, sala: r.eu.sala });
      setErro(null);
    }, (e: Error) => { if (vivo) setErro(e.message); });
    void ler();
    const t = setInterval(() => { void ler(); }, ATUALIZAR_MS);
    return () => { vivo = false; clearInterval(t); };
  }, [sessao.id, vez]);
  return { dados, erro, recarregar: () => setVez((n) => n + 1) };
}

// Chamados feitos nesta aba: o botão vira "Chamado" pelos 2 minutos em que a API não chama de novo.
const chamadosAte = new Map<string, number>();

function BotaoChamar({ sessao, amigo, sala }: { sessao: Sessao; amigo: AmigoNaLista; sala: MinhaSala | null }) {
  const agora = useAgora(5_000);
  const [, refazer] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  const ate = chamadosAte.get(amigo.usuario) ?? 0;
  if (sala && agora < ate) return <span className="chamar feito" role="status">Chamado</span>;
  async function chamar() {
    setOcupado(true);
    try {
      const r = await chamarParaMesa(sessao, amigo.usuario);
      chamadosAte.set(amigo.usuario, r.ate);
      avisarNaTela(`${amigo.apelido} foi chamado para a mesa.`);
    } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); refazer((n) => n + 1); }
  }
  return (
    <button className="chamar" disabled={!sala || ocupado} aria-describedby={sala ? undefined : 'dica-chamar'}
      aria-label={sala ? `Chamar ${amigo.apelido} para a mesa` : `Chamar ${amigo.apelido}. ${DICA_SEM_SALA}`} onClick={() => void chamar()}>
      Chamar
    </button>
  );
}

// Assistir a mesa do amigo: toda sala é aberta, e qualquer conta assiste.
function BotaoAssistir({ amigo }: { sessao: Sessao; amigo: AmigoNaLista; tipo: 'publica' | 'privada'; plano: Plano | null }) {
  return <button className="btn2 assistir" aria-label={`Assistir a mesa de ${amigo.apelido}`} onClick={() => ir({ tela: 'assistir', codigo: null, amigo: amigo.usuario })}>Assistir</button>;
}

function TextoDoStatus({ a, agora }: { a: AmigoNaLista; agora: number }) {
  const s = a.status;
  // O nome da sala corta com reticências; "5 de 6" (ou "1 vaga", com a partida andando) aparece sempre.
  if (s.tipo === 'sala') {
    const conta = s.vagas ? `${s.vagas} ${s.vagas === 1 ? 'vaga' : 'vagas'}` : `${s.sentados} de 6`;
    return <span className="status-amigo sala"><span className="nome-sala">Na {s.nome}</span><span>&nbsp;· {conta}</span></span>;
  }
  if (s.tipo === 'jogando') return <span className="status-amigo jogando">{s.nome ? `Jogando agora · ${s.nome}` : 'Jogando agora'}</span>;
  if (s.tipo === 'online') return <span className="status-amigo online">Online</span>;
  return <span className="status-amigo">{textoVisto(agora, s.em)}</span>;
}

// Na aba Amigos, a linha tem WhatsApp e Chamar para o trio, e as ações descem para a linha de baixo do nome. Na folha
// de chamar amigos da sala de espera, só Entrar e Chamar.
export function LinhaAmigo({ sessao, a, sala, agora, whatsapp = true, trio = whatsapp, plano = null }: {
  sessao: Sessao; a: AmigoNaLista; sala: MinhaSala | null; agora: number; whatsapp?: boolean; trio?: boolean; plano?: Plano | null;
}) {
  const s = a.status;
  const presente = s.tipo !== 'visto';
  const detalhe = [a.nivel ? `Nível ${a.nivel}` : null, a.divisao ? rotuloDaDivisao(a.divisao) : null].filter(Boolean).join(' · ');
  // Entrar: amigo numa sala aberta com vaga que não é a sua. Chamar: fora de sala (desabilitado) ou na sua sala
  // chamável, para quem ainda não está nela.
  const entrar = s.tipo === 'sala' && s.vaga && s.codigo !== sala?.codigo;
  // Assistir: amigo jogando agora numa sala com plateia. A sala privada é só para Prata e Ouro (cadeado e Planos).
  const assistir = s.tipo === 'jogando' && !!s.assistir ? s.assistir : null;
  const chamar = !(sala && !sala.chamavel) && !(s.tipo === 'sala' && sala && s.codigo === sala.codigo);
  return (
    <li className="linha-amigo">
      <BotaoPerfil usuario={a.usuario} apelido={a.apelido} className="pessoa-amigo">
        <span className="rosto-amigo">
          <Rosto perfil={{ apelido: a.apelido, avatar: a.avatar }} usuario={a.usuario} cor="var(--gold-600)" tamanho={44} />
          {presente && <span className="ponto-online" aria-hidden="true" />}
        </span>
        <span className="quem-amigo">
          <b>{a.apelido}</b>
          {a.trio && <small className="trio-amigo"><Escudo id={a.trio.escudo} tamanho={14} />{a.trio.nome}</small>}
          {detalhe && <small>{detalhe}</small>}
          <TextoDoStatus a={a} agora={agora} />
        </span>
      </BotaoPerfil>
      {(entrar || assistir || chamar || whatsapp || trio) && (
        <span className={'acoes-amigo' + (trio ? ' embaixo' : (entrar || assistir) && chamar ? ' duas' : '')}>
          {!trio && whatsapp && <BotaoWhatsapp sessao={sessao} usuario={a.usuario} apelido={a.apelido} liberado={a.whatsapp} />}
          {entrar && s.tipo === 'sala' && (
            <button className="btn2 entrar-amigo" aria-label={`Entrar na ${s.nome}`} onClick={() => irParaMesa({ tela: 'sala', codigo: s.codigo, vaga: 'amigo' })}>Entrar</button>
          )}
          {assistir && <BotaoAssistir sessao={sessao} amigo={a} tipo={assistir} plano={plano} />}
          {chamar && <BotaoChamar sessao={sessao} amigo={a} sala={sala} />}
          {trio && <BotaoTrio usuario={a.usuario} apelido={a.apelido} compacto />}
          {trio && whatsapp && <BotaoWhatsapp sessao={sessao} usuario={a.usuario} apelido={a.apelido} liberado={a.whatsapp} />}
        </span>
      )}
    </li>
  );
}

function Busca({ sessao }: { sessao: Sessao }) {
  const [termo, setTermo] = useState('');
  const [achados, setAchados] = useState<Encontrado[] | null>(null);
  const pedido = useRef(0);
  useEffect(() => {
    const t = termo.trim();
    if (t.length < 2) { setAchados(null); return; }
    const n = ++pedido.current;
    const espera = setTimeout(() => {
      buscarJogadores(sessao, t).then((r) => {
        if (n !== pedido.current) return;
        for (const j of r.jogadores) definirRelacao(j.usuario, j.relacao);
        setAchados(r.jogadores);
      }, () => { if (n === pedido.current) setAchados([]); });
    }, 300);
    return () => clearTimeout(espera);
  }, [termo, sessao.id]);
  return (
    <section className="busca-amigos" aria-label="Buscar jogadores">
      <label className="campo">
        <span className="rotulo-campo">Buscar por apelido</span>
        <input type="search" value={termo} maxLength={20} placeholder="Apelido de quem joga" autoComplete="off" onChange={(e) => setTermo(e.target.value)} />
      </label>
      {achados && (achados.length === 0
        ? <p className="nota" role="status">Ninguém com esse apelido.</p>
        : (
          <ul className="lista-pessoas" aria-label="Jogadores encontrados">
            {achados.map((j) => (
              <li key={j.usuario}>
                <BotaoPerfil usuario={j.usuario} apelido={j.apelido}>
                  <Rosto perfil={{ apelido: j.apelido, avatar: j.avatar }} usuario={j.usuario} cor="var(--line-600)" tamanho={40} />
                  <span className="nome"><b>{j.apelido}</b></span>
                </BotaoPerfil>
                <BotaoAmizade usuario={j.usuario} apelido={j.apelido} compacto />
                <BotaoTrio usuario={j.usuario} apelido={j.apelido} compacto />
              </li>
            ))}
          </ul>
        ))}
    </section>
  );
}

// Um pedido recebido some da lista quando a resposta muda a relação (Aceitar ou Recusar).
function LinhaPedido({ p }: { p: PedidoDeAmizade }) {
  const r = useRelacao(p.usuario);
  if (r && r !== 'pedido_recebido') return null;
  return (
    <li>
      <BotaoPerfil usuario={p.usuario} apelido={p.apelido}>
        <Rosto perfil={{ apelido: p.apelido, avatar: p.avatar }} usuario={p.usuario} cor="var(--gold-600)" tamanho={40} />
        <span className="nome"><b>{p.apelido}</b><small>quer ser seu amigo</small></span>
      </BotaoPerfil>
      <BotaoAmizade usuario={p.usuario} apelido={p.apelido} compacto />
    </li>
  );
}

function Pedidos({ sessao }: { sessao: Sessao }) {
  const [recebidos, setRecebidos] = useState<PedidoDeAmizade[]>([]);
  const { pedidos } = usePresenca(); // chegou pedido novo: lê de novo
  useEffect(() => {
    let vivo = true;
    lerPedidos(sessao).then((r) => {
      if (!vivo) return;
      for (const p of r.recebidos) definirRelacao(p.usuario, 'pedido_recebido');
      setRecebidos(r.recebidos);
    }, () => {});
    return () => { vivo = false; };
  }, [sessao.id, pedidos]);
  if (!recebidos.length) return null;
  return (
    <section className="secao-amigos" aria-label="Pedidos de amizade">
      <h2 className="rotulo">PEDIDOS DE AMIZADE <span className="dourado">· {recebidos.length}</span></h2>
      <ul className="lista-pessoas">{recebidos.map((p) => <LinhaPedido key={p.id} p={p} />)}</ul>
    </section>
  );
}

export function Amigos({ sessao, plano = null }: { sessao: Sessao; plano?: Plano | null }) {
  const { dados, erro, recarregar } = useAmigos(sessao);
  const agora = useAgora(30_000);
  if (sessao.anonimo) {
    return (
      <Tela>
        <header className="topo"><h1>Amigos</h1></header>
        <div className="cartao vazio-lista">
          <p>Guarde sua conta com Google para adicionar amigos, ver quem está online e chamar a turma para a mesa.</p>
          <button className="btn2" onClick={() => ir({ tela: 'perfil' })}>Ir para o perfil</button>
        </div>
      </Tela>
    );
  }
  return (
    <Tela painel>
      <header className="topo"><h1>Amigos</h1></header>
      <SeloNoTopo />
      <div className="painel-amigos">
      <Busca sessao={sessao} />
      <Pedidos sessao={sessao} />
      <section className="secao-amigos seus-amigos" aria-label="Seus amigos">
        <h2 className="rotulo">SEUS AMIGOS{dados && <span className="dourado"> · {dados.amigos.length}</span>}</h2>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {!dados && !erro && <p className="carregando" role="status">Carregando...</p>}
        {dados && dados.amigos.length > 0 && !dados.sala && <p id="dica-chamar" className="nota">{DICA_SEM_SALA}</p>}
        {dados && dados.amigos.length === 0 && (
          <div className="cartao vazio-lista">
            <span className="icone-vazio" aria-hidden="true"><IconeAmigos tamanho={28} /></span>
            <p>Você ainda não tem amigos aqui. Busque pelo apelido ou toque no rosto de quem jogou com você na mesa.</p>
          </div>
        )}
        {dados && dados.amigos.length > 0 && (
          <ul className="lista-amigos">{dados.amigos.map((a) => <LinhaAmigo key={a.usuario} sessao={sessao} a={a} sala={dados.sala} agora={agora} plano={plano} />)}</ul>
        )}
      </section>
      <ChaveWhatsapp sessao={sessao} aoMudar={recarregar} />
      </div>
    </Tela>
  );
}

// Sala de espera: "Chamar amigos" abre a lista de amigos numa folha, com o Chamar de cada um.
export function FolhaChamarAmigos({ sessao, aoFechar }: { sessao: Sessao; aoFechar: () => void }) {
  const { dados, erro } = useAmigos(sessao);
  const agora = useAgora(30_000);
  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <div className="folha simples folha-chamar" role="dialog" aria-modal="true" aria-labelledby="folha-chamar">
        <h2 id="folha-chamar">Chamar amigos</h2>
        <p className="nota">Cada amigo recebe o chamado com o Entrar desta sala.</p>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {!dados && !erro && <p className="carregando" role="status">Carregando...</p>}
        {dados && dados.amigos.length === 0 && <p className="nota">Você ainda não tem amigos aqui. Adicione quem jogou com você pelo rosto na mesa.</p>}
        {dados && dados.amigos.length > 0 && !dados.sala && <p id="dica-chamar" className="nota">{DICA_SEM_SALA}</p>}
        {dados && dados.amigos.length > 0 && (
          <ul className="lista-amigos">{dados.amigos.map((a) => <LinhaAmigo key={a.usuario} sessao={sessao} a={a} sala={dados.sala} agora={agora} whatsapp={false} />)}</ul>
        )}
        <button className="btn2" onClick={aoFechar}>Fechar</button>
      </div>
    </div>,
    document.body,
  );
}
